/**
 * SmartDateInput — user-friendly date picker for HHMS
 * ─────────────────────────────────────────────────────────────────────────────
 * Replaces the browser's native <input type="date"> which forces painful
 * month-by-month navigation when entering birth dates from decades past.
 *
 * Features
 * --------
 *  • Displays formatted date (e.g. "15 Jan 1975") in a plain text field
 *  • 📅 icon opens a 3-view popup:
 *      YEAR VIEW  → 12-year grid, prev/next decade, type-to-jump input
 *      MONTH VIEW → 12 month tiles for the chosen year
 *      DAY VIEW   → standard calendar grid
 *  • Clicking the month/year header cycles back through views
 *  • Keyboard-friendly: Escape closes, Enter confirms highlighted day
 *  • Fully controlled: value="YYYY-MM-DD", onChange fires a synthetic
 *    { target: { value: "YYYY-MM-DD" } } — drop-in for <input type="date">
 *  • Respects min / max props (YYYY-MM-DD strings)
 *  • "Clear" button when a date is set
 *
 * Usage (identical to current native input)
 * -----------------------------------------
 *   <Input type="date" value={form.birthdate} onChange={upd('birthdate')} />
 *   // ↑ automatically routed here via the Input wrapper in primitives.js
 *
 *   // Or directly:
 *   <SmartDateInput value="1975-01-15" onChange={e => setState(e.target.value)} />
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { COLOR, RADIUS, FONT, baseInputStyle } from './tokens';

// ─── constants ────────────────────────────────────────────────────────────────

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun',
                      'Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS_HEADER  = ['Mo','Tu','We','Th','Fr','Sa','Su'];

const VIEWS = { YEAR: 'year', MONTH: 'month', DAY: 'day' };

// ─── helpers ──────────────────────────────────────────────────────────────────

/** Parse "YYYY-MM-DD" → { year, month (1-12), day } or null */
function parseVal(v) {
  if (!v || typeof v !== 'string') return null;
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { year: +m[1], month: +m[2], day: +m[3] };
}

/** Format { year, month, day } → "YYYY-MM-DD" */
function toVal(y, m, d) {
  return `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
}

/** Format { year, month, day } → "15 Jan 1975" */
function display(parsed) {
  if (!parsed) return '';
  return `${parsed.day} ${MONTHS_SHORT[parsed.month - 1]} ${parsed.year}`;
}

/** Days in a month (accounts for leap years) */
function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

/** Day-of-week index (0=Mon … 6=Sun) for the 1st of month */
function firstDayOfWeek(year, month) {
  const d = new Date(year, month - 1, 1).getDay(); // 0=Sun
  return d === 0 ? 6 : d - 1; // convert to Mon-based
}

function cmp(y1,m1,d1, y2,m2,d2) {
  if (y1 !== y2) return y1 - y2;
  if (m1 !== m2) return m1 - m2;
  return d1 - d2;
}

function today() {
  const n = new Date();
  return { year: n.getFullYear(), month: n.getMonth() + 1, day: n.getDate() };
}

// ─── styles ───────────────────────────────────────────────────────────────────

const S = {
  wrapper:  { position: 'relative', display: 'block', width: '100%' },
  field:    {
    ...baseInputStyle,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    cursor: 'pointer', userSelect: 'none', gap: 6,
  },
  placeholder: { color: COLOR.grayLight, fontStyle: 'italic' },
  popup: {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 9999,
    background: '#fff', border: `1px solid ${COLOR.grayBorder}`,
    borderRadius: RADIUS.lg, boxShadow: '0 8px 24px rgba(13,27,46,.14)',
    width: 280, padding: 0, overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 12px 8px',
    borderBottom: `1px solid ${COLOR.grayBorder}`,
    background: COLOR.grayBg,
  },
  navBtn: {
    background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px',
    borderRadius: RADIUS.sm, fontSize: 14, color: COLOR.gray,
    lineHeight: 1,
  },
  headerLabel: {
    fontSize: FONT.size.base, fontWeight: FONT.weight.bold, color: COLOR.text,
    cursor: 'pointer', padding: '2px 6px', borderRadius: RADIUS.sm,
    border: 'none', background: 'none',
  },
  grid12: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 4, padding: '10px 10px 12px',
  },
  grid7: {
    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
    padding: '0 10px 10px',
  },
  tile: (active, today, disabled, muted) => ({
    border: 'none', borderRadius: RADIUS.md, padding: '7px 4px',
    cursor: disabled ? 'not-allowed' : 'pointer', fontSize: FONT.size.base,
    fontWeight: active ? FONT.weight.bold : FONT.weight.normal,
    background: active ? COLOR.blue : today ? COLOR.blueBg : 'none',
    color: disabled ? COLOR.grayLight
         : active   ? '#fff'
         : today    ? COLOR.blue
         : muted    ? COLOR.grayLight
         : COLOR.text,
    textAlign: 'center', transition: 'background .12s',
  }),
  dayHeader: {
    fontSize: FONT.size.xs, fontWeight: FONT.weight.bold,
    color: COLOR.textMuted, textAlign: 'center', padding: '8px 0 4px',
    textTransform: 'uppercase',
  },
  footer: {
    borderTop: `1px solid ${COLOR.grayBorder}`,
    padding: '8px 12px', display: 'flex', gap: 8, justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerBtn: {
    background: 'none', border: 'none', cursor: 'pointer', padding: '3px 8px',
    borderRadius: RADIUS.sm, fontSize: FONT.size.sm, fontWeight: FONT.weight.semi,
  },
  jumpInput: {
    border: `1px solid ${COLOR.grayBorder}`, borderRadius: RADIUS.sm,
    padding: '4px 8px', fontSize: FONT.size.sm, width: 70,
    fontFamily: FONT.family, color: COLOR.text, outline: 'none',
    textAlign: 'center',
  },
};

// ─── component ────────────────────────────────────────────────────────────────

export function SmartDateInput({
  value,
  onChange,
  placeholder = 'Select date',
  min,
  max,
  disabled = false,
  style: extraStyle,
  id,
  name,
}) {
  const parsed    = parseVal(value);
  const minParsed = parseVal(min);
  const maxParsed = parseVal(max);

  const [open,    setOpen]    = useState(false);
  const [view,    setView]    = useState(VIEWS.DAY);
  // cursor = what month/year is currently displayed (not the selected value)
  const [cursor,  setCursor]  = useState(() => {
    const p = parseVal(value);
    const t = today();
    return { year: p?.year || t.year, month: p?.month || t.month };
  });
  const [jumpVal, setJumpVal] = useState('');
  const wrapRef = useRef(null);

  // Keep cursor in sync when value changes externally
  useEffect(() => {
    const p = parseVal(value);
    if (p) setCursor({ year: p.year, month: p.month });
  }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const emit = useCallback((y, m, d) => {
    const v = toVal(y, m, d);
    onChange?.({ target: { value: v, name } });
    setOpen(false);
  }, [onChange, name]);

  const clear = useCallback((e) => {
    e.stopPropagation();
    onChange?.({ target: { value: '', name } });
  }, [onChange, name]);

  const openPicker = () => {
    if (disabled) return;
    setView(VIEWS.DAY);
    setJumpVal('');
    setOpen(true);
  };

  const isDisabledDate = (y, m, d) => {
    if (minParsed && cmp(y,m,d, minParsed.year,minParsed.month,minParsed.day) < 0) return true;
    if (maxParsed && cmp(y,m,d, maxParsed.year,maxParsed.month,maxParsed.day) > 0) return true;
    return false;
  };

  const isDisabledMonth = (y, m) => {
    if (minParsed && (y < minParsed.year || (y === minParsed.year && m < minParsed.month))) return true;
    if (maxParsed && (y > maxParsed.year || (y === maxParsed.year && m > maxParsed.month))) return true;
    return false;
  };

  const isDisabledYear = (y) => {
    if (minParsed && y < minParsed.year) return true;
    if (maxParsed && y > maxParsed.year) return true;
    return false;
  };

  // ── Year grid (12 years centred on current decade) ─────────────────────────
  const decadeStart = Math.floor(cursor.year / 12) * 12;

  const yearGrid = () => {
    const years = Array.from({ length: 12 }, (_, i) => decadeStart + i);
    const t = today();
    return (
      <>
        <div style={S.header}>
          <button style={S.navBtn}
            onClick={() => setCursor(c => ({ ...c, year: c.year - 12 }))}>‹</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: FONT.size.sm, color: COLOR.text, fontWeight: FONT.weight.bold }}>
              {decadeStart}–{decadeStart + 11}
            </span>
            <input
              style={S.jumpInput}
              placeholder="Jump…"
              value={jumpVal}
              maxLength={4}
              onChange={e => {
                const v = e.target.value.replace(/\D/g,'');
                setJumpVal(v);
                if (v.length === 4) {
                  const y = parseInt(v, 10);
                  if (y >= 1900 && y <= 2100) setCursor(c => ({ ...c, year: y }));
                }
              }}
              onClick={e => e.stopPropagation()}
              title="Type a year to jump"
            />
          </div>
          <button style={S.navBtn}
            onClick={() => setCursor(c => ({ ...c, year: c.year + 12 }))}>›</button>
        </div>
        <div style={S.grid12}>
          {years.map(y => {
            const isActive   = parsed?.year === y;
            const isToday    = t.year === y;
            const isDisabled = isDisabledYear(y);
            return (
              <button key={y}
                style={{
                  ...S.tile(isActive, isToday, isDisabled, false),
                  padding: '9px 0',
                }}
                disabled={isDisabled}
                onMouseEnter={e => { if (!isActive && !isDisabled) e.currentTarget.style.background = COLOR.blueBg; }}
                onMouseLeave={e => { if (!isActive && !isDisabled) e.currentTarget.style.background = 'none'; }}
                onClick={() => {
                  setCursor(c => ({ ...c, year: y }));
                  setView(VIEWS.MONTH);
                  setJumpVal('');
                }}
              >
                {y}
              </button>
            );
          })}
        </div>
        <div style={S.footer}>
          <button style={{ ...S.footerBtn, color: COLOR.blue }}
            onClick={() => { const t2 = today(); setCursor({ year: t2.year, month: t2.month }); }}>
            Today's year
          </button>
        </div>
      </>
    );
  };

  // ── Month grid ─────────────────────────────────────────────────────────────
  const monthGrid = () => {
    const t = today();
    return (
      <>
        <div style={S.header}>
          <button style={S.navBtn}
            onClick={() => setCursor(c => ({ ...c, year: c.year - 1 }))}>‹</button>
          <button style={S.headerLabel}
            onClick={() => setView(VIEWS.YEAR)}
            title="Pick a different year">
            {cursor.year} ▾
          </button>
          <button style={S.navBtn}
            onClick={() => setCursor(c => ({ ...c, year: c.year + 1 }))}>›</button>
        </div>
        <div style={S.grid12}>
          {MONTHS_SHORT.map((label, i) => {
            const m          = i + 1;
            const isActive   = parsed?.year === cursor.year && parsed?.month === m;
            const isToday    = t.year === cursor.year && t.month === m;
            const isDisabled = isDisabledMonth(cursor.year, m);
            return (
              <button key={m}
                style={{ ...S.tile(isActive, isToday, isDisabled, false), padding: '8px 4px' }}
                disabled={isDisabled}
                onMouseEnter={e => { if (!isActive && !isDisabled) e.currentTarget.style.background = COLOR.blueBg; }}
                onMouseLeave={e => { if (!isActive && !isDisabled) e.currentTarget.style.background = 'none'; }}
                onClick={() => {
                  setCursor(c => ({ ...c, month: m }));
                  setView(VIEWS.DAY);
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
        <div style={S.footer}>
          <span style={{ fontSize: FONT.size.xs, color: COLOR.textMuted }}>
            Select a month
          </span>
        </div>
      </>
    );
  };

  // ── Day calendar ───────────────────────────────────────────────────────────
  const dayCalendar = () => {
    const t       = today();
    const offset  = firstDayOfWeek(cursor.year, cursor.month);
    const numDays = daysInMonth(cursor.year, cursor.month);
    const cells   = Array.from({ length: offset + numDays }, (_, i) =>
      i < offset ? null : i - offset + 1
    );
    // pad to full weeks
    while (cells.length % 7 !== 0) cells.push(null);

    return (
      <>
        <div style={S.header}>
          <button style={S.navBtn} onClick={() => setCursor(c => {
            const m = c.month === 1 ? 12 : c.month - 1;
            const y = c.month === 1 ? c.year - 1 : c.year;
            return { year: y, month: m };
          })}>‹</button>
          <button style={S.headerLabel}
            onClick={() => setView(VIEWS.MONTH)}
            title="Pick month / year">
            {MONTHS[cursor.month - 1]} {cursor.year} ▾
          </button>
          <button style={S.navBtn} onClick={() => setCursor(c => {
            const m = c.month === 12 ? 1 : c.month + 1;
            const y = c.month === 12 ? c.year + 1 : c.year;
            return { year: y, month: m };
          })}>›</button>
        </div>

        {/* Day-of-week headers */}
        <div style={{ ...S.grid7, paddingBottom: 0 }}>
          {DAYS_HEADER.map(d => (
            <div key={d} style={S.dayHeader}>{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div style={S.grid7}>
          {cells.map((day, idx) => {
            if (!day) return <div key={`e${idx}`} />;
            const isActive   = parsed?.year === cursor.year && parsed?.month === cursor.month && parsed?.day === day;
            const isTodayDay = t.year === cursor.year && t.month === cursor.month && t.day === day;
            const isDisabled = isDisabledDate(cursor.year, cursor.month, day);
            return (
              <button key={day}
                style={{ ...S.tile(isActive, isTodayDay, isDisabled, false), padding: '6px 0' }}
                disabled={isDisabled}
                onMouseEnter={e => { if (!isActive && !isDisabled) e.currentTarget.style.background = COLOR.blueBg; }}
                onMouseLeave={e => { if (!isActive && !isDisabled) e.currentTarget.style.background = 'none'; }}
                onClick={() => emit(cursor.year, cursor.month, day)}
              >
                {day}
              </button>
            );
          })}
        </div>

        <div style={S.footer}>
          <button style={{ ...S.footerBtn, color: COLOR.blue }}
            onClick={() => {
              const t2 = today();
              if (!isDisabledDate(t2.year, t2.month, t2.day)) {
                setCursor({ year: t2.year, month: t2.month });
                emit(t2.year, t2.month, t2.day);
              } else {
                setCursor({ year: t2.year, month: t2.month });
              }
            }}>
            Today
          </button>
          {parsed && (
            <button style={{ ...S.footerBtn, color: COLOR.red }}
              onClick={e => { setOpen(false); clear(e); }}>
              Clear
            </button>
          )}
        </div>
      </>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div ref={wrapRef} style={{ ...S.wrapper, ...(extraStyle || {}) }}>
      {/* Trigger field */}
      <div
        id={id}
        role="button"
        tabIndex={disabled ? -1 : 0}
        style={{
          ...S.field,
          background: disabled ? COLOR.grayBg : '#fff',
          cursor: disabled ? 'not-allowed' : 'pointer',
          borderColor: open ? COLOR.blue : COLOR.grayBorder,
          boxShadow: open ? `0 0 0 3px rgba(123,30,60,.15)` : 'none',
          ...(extraStyle || {}),
        }}
        onClick={openPicker}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') openPicker(); }}
      >
        <span style={parsed ? { color: COLOR.text } : S.placeholder}>
          {parsed ? display(parsed) : placeholder}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {parsed && !disabled && (
            <span
              role="button"
              title="Clear date"
              onClick={clear}
              style={{
                fontSize: 12, color: COLOR.grayLight, cursor: 'pointer',
                padding: '0 2px', borderRadius: RADIUS.sm, lineHeight: 1,
              }}
              onMouseEnter={e => e.currentTarget.style.color = COLOR.red}
              onMouseLeave={e => e.currentTarget.style.color = COLOR.grayLight}
            >
              ✕
            </span>
          )}
          <span style={{ fontSize: 15, color: open ? COLOR.blue : COLOR.grayLight }}>📅</span>
        </span>
      </div>

      {/* Popup */}
      {open && (
        <div style={S.popup} onMouseDown={e => e.stopPropagation()}>
          {view === VIEWS.YEAR  && yearGrid()}
          {view === VIEWS.MONTH && monthGrid()}
          {view === VIEWS.DAY   && dayCalendar()}
        </div>
      )}

      {/* Hidden native input for form submission compatibility */}
      <input
        type="hidden"
        name={name}
        value={value || ''}
      />
    </div>
  );
}

export default SmartDateInput;
